import useBreakpoint, { ScreenSize } from "@@/hooks/device/use-breakpoint";
import useElementSize from "@@/hooks/ui/use-element-size";
import cn from "classnames";
import React, {
  CSSProperties, useEffect, useRef, useState,
} from "react";
import { useSideHeader } from "../SideHeader/context";
import ScrollerContainer from "./container";
import css from "./container/index.module.scss";
import ScrollerContext, { ScrollerContextValue } from "./context";
import SolidBar from "./solid-bar";

export type ScrollerProps = {
  className?: string;
  children: React.ReactNode | React.ReactNode[];
  fullWidthItemsMobile?: boolean;
  fullPageStretch?: boolean;
};

const Scroller = ({
  className,
  children,
  fullWidthItemsMobile = true,
  fullPageStretch,
}: ScrollerProps) => {
  const sideHeader = useSideHeader();
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasItemOverflow, setHasItemOverflow] = useState(false);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const scrollingEl = useRef<HTMLOListElement>(null);
  const scrollerEl = useRef<HTMLDivElement>(null);
  const { clientWidth } = useElementSize(scrollerEl);
  const breakpoint = useBreakpoint() || ScreenSize.SMALL;

  const fullWidthItemsFinal = (breakpoint === ScreenSize.SMALL && fullWidthItemsMobile);

  const [viewportWidth, setViewportWidth] = useState(0);
  useEffect(() => {
    const measure = () => setViewportWidth(document.documentElement.clientWidth);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const getItemScrollDistance = () => {
    const el = scrollingEl.current;
    if (!el) return 0;
    const firstItem = el.querySelector("li");
    if (!firstItem) return el.clientWidth * 0.5;
    const gap = parseInt(getComputedStyle(el).columnGap || "0", 10);
    return firstItem.offsetWidth + gap;
  };

  const gotoNext = () => {
    if (!scrollingEl.current) return;
    scrollingEl.current.scrollBy({ left: getItemScrollDistance(), behavior: "smooth" });
  };

  const gotoPrev = () => {
    if (!scrollingEl.current) return;
    scrollingEl.current.scrollBy({ left: -getItemScrollDistance(), behavior: "smooth" });
  };

  const context: ScrollerContextValue = {
    scrollLeft,
    hasItemOverflow,
    isAtStart,
    isAtEnd,
    fullPageStretch,
    fullWidthItemsFinal,
    sideHeader,
    scrollingElRef: scrollingEl,
    gotoNext,
    gotoPrev,
  };

  useEffect(() => {
    const el = scrollingEl.current;
    if (!el) return undefined;
    const handler = () => {
      setScrollLeft(el.scrollLeft);
      setIsAtStart(el.scrollLeft < 1);
      setIsAtEnd(Math.ceil(el.scrollLeft + el.clientWidth) >= el.scrollWidth);
    };
    el.addEventListener("scroll", handler, { passive: true });
    return () => el.removeEventListener("scroll", handler);
  }, []);

  const checkOverflow = () => {
    const el = scrollingEl.current;
    if (!el) return;
    const THRESHOLD = 10;
    setHasItemOverflow(el.scrollWidth > el.clientWidth + THRESHOLD);
  };

  useEffect(() => {
    const el = scrollingEl.current;
    if (!el) return undefined;
    let initialReset = true;
    const ro = new ResizeObserver(() => {
      checkOverflow();
      if (initialReset) {
        initialReset = false;
        el.style.scrollSnapType = "none";
        el.style.scrollBehavior = "auto";
        el.scrollLeft = 0;
        requestAnimationFrame(() => {
          el.style.scrollSnapType = "";
          el.style.scrollBehavior = "";
        });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    checkOverflow();
  }, [clientWidth]);

  return (
    <ScrollerContext.Provider value={context}>
      <div
        className={cn(css["hbd-scroller"], {
          [css["hbd-scroller--full-width-items"]]: fullWidthItemsFinal,
        })}
        ref={scrollerEl}
        style={{
          "--scroller-width": `${clientWidth || 0}px`,
          "--viewport-width": `${viewportWidth}px`,
        } as CSSProperties}
      >
        {children && (
          <>
            <ScrollerContainer
              ref={scrollingEl}
              className={className}
              sideHeader={sideHeader}
            >
              {children}
            </ScrollerContainer>
            {hasItemOverflow && (
              <SolidBar sideHeader={sideHeader} />
            )}
          </>
        )}
      </div>
    </ScrollerContext.Provider>
  );
};

export default Scroller;
export { default as ScrollerItem } from "./item";
